"use strict";
/**
 * Slippage Controls
 *
 * Manages all slippage-related UI on the order page:
 * - Single (unified) slippage slider and ±1 step buttons
 * - Independent A/B slippage sliders with per-side step buttons
 * - Toggle between unified and independent modes
 *
 * Each control persists its value to `TradingSettings` via
 * `updateState` + `saveSettingsToStorage` and synchronises the
 * corresponding display element through the UI layer.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupUnifiedSlippageControls = setupUnifiedSlippageControls;
exports.setupIndependentSlippageControls = setupIndependentSlippageControls;
exports.initSlippageState = initSlippageState;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * Generic slider-step helper.
 * Reads the current `<input type="range">` value, clamps after applying
 * `delta`, writes back to the slider, and calls `onUpdate` with the new value.
 *
 * @param slider   - The range input element
 * @param delta    - Increment (+1) or decrement (-1) step
 * @param onUpdate - Callback invoked with the clamped new value
 */
function stepSlider(slider, delta, onUpdate) {
    if (!slider)
        return;
    const currentValue = parseInt(slider.value, 10);
    const minValue = parseInt(slider.min, 10);
    const maxValue = parseInt(slider.max, 10);
    const newValue = Math.max(minValue, Math.min(maxValue, currentValue + delta));
    slider.value = newValue.toString();
    onUpdate(newValue);
}
/**
 * Persist a single trading-setting key and refresh the corresponding slider.
 */
function persistSlippageSetting(key, value) {
    const state = (0, state_1.getState)();
    (0, state_1.updateState)({
        tradingSettings: { ...state.tradingSettings, [key]: value },
    });
    (0, state_1.saveSettingsToStorage)();
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Wire up the unified (single) slippage slider and its ±1 step buttons.
 *
 * @param slippageSlider       - The `<input type="range">` for unified slippage
 * @param slippageDecrementBtn - The "−" button
 * @param slippageIncrementBtn - The "+" button
 */
function setupUnifiedSlippageControls(slippageSlider, slippageDecrementBtn, slippageIncrementBtn) {
    if (slippageSlider) {
        slippageSlider.addEventListener("input", (e) => {
            const newSlippage = parseInt(e.target.value, 10);
            persistSlippageSetting("slippageTolerancePercent", newSlippage);
            (0, ui_1.updateSlippageSlider)(newSlippage);
        });
    }
    const updateSlippageByStep = (delta) => {
        stepSlider(slippageSlider, delta, (newValue) => {
            persistSlippageSetting("slippageTolerancePercent", newValue);
            (0, ui_1.updateSlippageSlider)(newValue);
        });
    };
    slippageDecrementBtn?.addEventListener("click", () => updateSlippageByStep(-1));
    slippageIncrementBtn?.addEventListener("click", () => updateSlippageByStep(1));
}
/**
 * Wire up the independent (A/B) slippage sliders, step buttons, and the
 * toggle buttons that switch between unified and independent modes.
 *
 * @param elements - An object containing all relevant DOM references
 */
function setupIndependentSlippageControls(elements) {
    const { independentSlippageToggle, independentSlippageToggleBack, slippageSliderA, slippageSliderB, slippageADecrementBtn, slippageAIncrementBtn, slippageBDecrementBtn, slippageBIncrementBtn, } = elements;
    // Toggle between unified ↔ independent modes
    const toggleIndependentSlippage = (enable) => {
        const state = (0, state_1.getState)();
        const updatedSettings = {
            ...state.tradingSettings,
            independentSlippage: enable,
        };
        // When enabling independent mode, initialise A/B from the single slider
        if (enable) {
            updatedSettings.slippageTolerancePercentA =
                state.tradingSettings.slippageTolerancePercent;
            updatedSettings.slippageTolerancePercentB =
                state.tradingSettings.slippageTolerancePercent;
        }
        (0, state_1.updateState)({ tradingSettings: updatedSettings });
        (0, state_1.saveSettingsToStorage)();
        (0, ui_1.updateIndependentSlippageVisibility)(enable);
        if (enable) {
            (0, ui_1.updateIndependentSlippageSlider)("a", updatedSettings.slippageTolerancePercentA);
            (0, ui_1.updateIndependentSlippageSlider)("b", updatedSettings.slippageTolerancePercentB);
        }
    };
    independentSlippageToggle?.addEventListener("click", () => toggleIndependentSlippage(true));
    independentSlippageToggleBack?.addEventListener("click", () => toggleIndependentSlippage(false));
    // Side A slider input
    if (slippageSliderA) {
        slippageSliderA.addEventListener("input", (e) => {
            const newSlippage = parseInt(e.target.value, 10);
            persistSlippageSetting("slippageTolerancePercentA", newSlippage);
            (0, ui_1.updateIndependentSlippageSlider)("a", newSlippage);
        });
    }
    // Side B slider input
    if (slippageSliderB) {
        slippageSliderB.addEventListener("input", (e) => {
            const newSlippage = parseInt(e.target.value, 10);
            persistSlippageSetting("slippageTolerancePercentB", newSlippage);
            (0, ui_1.updateIndependentSlippageSlider)("b", newSlippage);
        });
    }
    // Side A/B step buttons
    const updateIndependentSlippageByStep = (side, delta) => {
        const slider = side === "a" ? slippageSliderA : slippageSliderB;
        const settingKey = side === "a"
            ? "slippageTolerancePercentA"
            : "slippageTolerancePercentB";
        stepSlider(slider, delta, (newValue) => {
            persistSlippageSetting(settingKey, newValue);
            (0, ui_1.updateIndependentSlippageSlider)(side, newValue);
        });
    };
    slippageADecrementBtn?.addEventListener("click", () => updateIndependentSlippageByStep("a", -1));
    slippageAIncrementBtn?.addEventListener("click", () => updateIndependentSlippageByStep("a", 1));
    slippageBDecrementBtn?.addEventListener("click", () => updateIndependentSlippageByStep("b", -1));
    slippageBIncrementBtn?.addEventListener("click", () => updateIndependentSlippageByStep("b", 1));
}
/**
 * Initialise slippage UI to reflect the persisted state on page load.
 * Should be called once during `setupOrderPage`.
 */
function initSlippageState() {
    const { tradingSettings } = (0, state_1.getState)();
    const isIndependent = tradingSettings.independentSlippage === true;
    (0, ui_1.updateIndependentSlippageVisibility)(isIndependent);
    if (isIndependent) {
        (0, ui_1.updateIndependentSlippageSlider)("a", tradingSettings.slippageTolerancePercentA ??
            tradingSettings.slippageTolerancePercent);
        (0, ui_1.updateIndependentSlippageSlider)("b", tradingSettings.slippageTolerancePercentB ??
            tradingSettings.slippageTolerancePercent);
    }
}
